import { Command } from "cmdk";
import { Building2, LoaderCircle } from "lucide-react";
import type { ReactNode } from "react";

import { taskTarget, type LinkTarget } from "@/features/collaboration/lib/links";
import { ServiceTile } from "@/features/projects/components/ServiceIcon";
import type { SearchResults } from "@/features/search/api";
import { resultValue } from "@/features/search/lib/paletteFilter";
import type { Schemas } from "@/shared/api/client";
import { useT } from "@/shared/i18n/useT";
import { Avatar } from "@/shared/ui/Avatar";
import { StatusIcon } from "@/shared/ui/StatusIcon";

type ServiceLine = Schemas["ServiceLine"];

const SERVICES: readonly string[] = ["web", "mobile", "automation", "marketing"];

function isServiceLine(value: string): value is ServiceLine {
  return SERVICES.includes(value);
}

const ITEM =
  "flex h-9 cursor-pointer items-center gap-2.5 rounded-lg px-2 text-[14px] text-ink data-[selected=true]:bg-surface-hover";

/** A result row; paletteFilter lets it through, since the server already matched it. */
function ResultItem({
  value,
  onSelect,
  children,
}: {
  value: string;
  onSelect: () => void;
  children: ReactNode;
}) {
  return (
    <Command.Item value={value} onSelect={onSelect} className={ITEM}>
      {children}
    </Command.Item>
  );
}

interface SearchResultGroupsProps {
  results: SearchResults | undefined;
  /** A request for the current query is on its way. */
  loading: boolean;
  /** Go somewhere and close the palette. */
  onOpen: (target: LinkTarget) => void;
}

/** The server's matches for the palette's query: projects, tasks, clients and people. */
export function SearchResultGroups({ results, loading, onOpen }: SearchResultGroupsProps) {
  const t = useT();
  return (
    <>
      {loading ? (
        <Command.Loading>
          <div className="flex h-9 items-center gap-2.5 px-2 text-[14px] text-ink-muted">
            <LoaderCircle className="size-4 animate-spin" aria-hidden="true" />
            {t("search.loading")}
          </div>
        </Command.Loading>
      ) : null}
      {results?.projects.length ? (
        <Command.Group heading={t("palette.projects")}>
          {results.projects.map((project) => (
            <ResultItem
              key={project.key}
              value={resultValue("project", project.key)}
              onSelect={() => {
                onOpen({ to: `/projects/${project.key}` });
              }}
            >
              {isServiceLine(project.service) ? (
                <ServiceTile service={project.service} size="sm" />
              ) : null}
              <span className="user-text min-w-0 flex-1 truncate">{project.name}</span>
              <span dir="ltr" className="font-mono text-[12px] text-ink-subtle">
                {project.key}
              </span>
            </ResultItem>
          ))}
        </Command.Group>
      ) : null}
      {results?.tasks.length ? (
        <Command.Group heading={t("search.tasks")}>
          {results.tasks.map((task) => (
            <ResultItem
              key={task.id}
              value={resultValue("task", task.id)}
              onSelect={() => {
                onOpen(taskTarget(task.project?.key ?? null, task.reference));
              }}
            >
              <StatusIcon status={task.status} />
              <span dir="ltr" className="shrink-0 font-mono text-[12.5px] text-ink-subtle">
                {task.reference}
              </span>
              <span className="user-text min-w-0 flex-1 truncate">{task.title}</span>
            </ResultItem>
          ))}
        </Command.Group>
      ) : null}
      {results?.clients.length ? (
        <Command.Group heading={t("search.clients")}>
          {results.clients.map((client) => (
            <ResultItem
              key={client.id}
              value={resultValue("client", client.id)}
              onSelect={() => {
                onOpen({ to: `/clients/${client.id}` });
              }}
            >
              <Building2 className="size-4 text-ink-muted" aria-hidden="true" />
              <span className="user-text min-w-0 flex-1 truncate">{client.name}</span>
            </ResultItem>
          ))}
        </Command.Group>
      ) : null}
      {results?.people.length ? (
        <Command.Group heading={t("search.people")}>
          {results.people.map((person) => (
            <ResultItem
              key={person.id}
              value={resultValue("person", person.id)}
              onSelect={() => {
                onOpen({ to: `/team?member=${person.id}` });
              }}
            >
              <Avatar name={person.name} src={person.avatar_url} className="size-5 text-[10.5px]" />
              <span className="user-text min-w-0 flex-1 truncate">{person.name}</span>
              {person.role ? (
                <span className="shrink-0 text-[12.5px] text-ink-subtle">
                  {t(`role.${person.role}`)}
                </span>
              ) : null}
            </ResultItem>
          ))}
        </Command.Group>
      ) : null}
    </>
  );
}
